import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createNutritionPlan } from '../../../services/nutrition';
import DaysEditorStep from './editor/DaysEditorStep';
import PlanDetailsStep from './editor/PlanDetailsStep';
import RestrictionsReviewStep from './editor/RestrictionsReviewStep';
import {
    buildNutritionPlanPayload,
    createNutritionPlanFormState,
    isDayEmpty
} from './nutritionPlanModel';
import { nutritionPlanReducer } from './nutritionPlanReducer';
import { validateNutritionPlan, validatePlanDetails } from './nutritionPlanValidation';

const dayIssueKeys = new Set([
    'nutrition.validation.daysMismatch',
    'nutrition.validation.duplicateDays',
    'nutrition.validation.dayObjectiveRequired',
    'nutrition.validation.dayMealRequired',
    'nutrition.validation.mealDetailsRequired',
    'nutrition.validation.mealOptionRequired',
    'nutrition.validation.optionIncomplete',
    'nutrition.validation.foodIncomplete',
    'nutrition.validation.duplicateOptionIds'
]);

const CreateNutritionPlan = ({
    initialTitle = '',
    initialDescription = '',
    initialData = null
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const initialState = useMemo(
        () =>
            createNutritionPlanFormState({
                name: initialTitle,
                description: initialDescription,
                data: initialData
            }),
        [initialData, initialDescription, initialTitle]
    );
    const initialPayload = useMemo(
        () => JSON.stringify(buildNutritionPlanPayload(initialState)),
        [initialState]
    );
    const [state, dispatch] = useReducer(nutritionPlanReducer, initialState);
    const [activeStep, setActiveStep] = useState(0);
    const [fieldErrors, setFieldErrors] = useState({});
    const [issues, setIssues] = useState([]);
    const [requestedDayId, setRequestedDayId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    const currentPayload = useMemo(() => buildNutritionPlanPayload(state), [state]);
    const isDirty = JSON.stringify(currentPayload) !== initialPayload;
    const steps = [
        t('nutrition.basicInfo'),
        t('nutrition.editor.daysAndMeals'),
        t('nutrition.editor.restrictionsAndReview')
    ];

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!isDirty || submitting) return;
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, submitting]);

    const syncDuration = () => {
        const duration = Number(state.durationDays);
        const removedDays = state.days.filter(
            (day) => Number(day.dayNumber) > duration && !isDayEmpty(day)
        );

        if (
            removedDays.length > 0 &&
            !window.confirm(
                t('nutrition.editor.reduceDurationConfirm', {
                    count: removedDays.length
                })
            )
        ) {
            return false;
        }

        dispatch({ type: 'syncDays' });
        return true;
    };

    const handleNext = () => {
        setIssues([]);
        setSubmitError(false);

        if (activeStep === 0) {
            const errors = validatePlanDetails(state);
            setFieldErrors(errors);
            if (Object.keys(errors).length > 0 || !syncDuration()) return;
        }

        if (activeStep === 1) {
            const dayIssues = validateNutritionPlan(state).filter((issue) =>
                dayIssueKeys.has(issue.messageKey)
            );
            if (dayIssues.length > 0) {
                setIssues(dayIssues);
                setRequestedDayId(dayIssues.find((issue) => issue.dayId)?.dayId || null);
                return;
            }
        }

        setActiveStep((step) => step + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBack = () => {
        setIssues([]);
        setActiveStep((step) => step - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancel = () => {
        if (!isDirty || window.confirm(t('nutrition.editor.discardConfirm'))) {
            navigate('/nutricao');
        }
    };

    const handleCreate = async () => {
        const validationIssues = validateNutritionPlan(state);
        if (validationIssues.length > 0) {
            setIssues(validationIssues);
            const dayIssue = validationIssues.find((issue) => issue.dayId);
            if (dayIssue) {
                setRequestedDayId(dayIssue.dayId);
                setActiveStep(1);
            }
            return;
        }

        setSubmitting(true);
        setSubmitError(false);
        try {
            await createNutritionPlan(currentPayload);
            navigate('/nutricao', {
                replace: true,
                state: { nutritionPlanCreated: true }
            });
        } catch {
            setSubmitError(true);
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 1280, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h3">{t('nutrition.create')}</Typography>
                    <Typography color="text.secondary">
                        {t('nutrition.editor.createSubtitle')}
                    </Typography>
                </Box>
            </Stack>

            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {issues.length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography fontWeight={600} gutterBottom>
                            {t('nutrition.validation.fixBeforeContinuing')}
                        </Typography>
                        <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
                            {issues.slice(0, 6).map((issue, index) => (
                                <li key={`${issue.messageKey}-${index}`}>
                                    {t(issue.messageKey, issue.values)}
                                </li>
                            ))}
                        </Box>
                        {issues.length > 6 && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {t('nutrition.validation.moreIssues', {
                                    count: issues.length - 6
                                })}
                            </Typography>
                        )}
                    </Alert>
                )}

                {submitError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {t('nutrition.createError')}
                    </Alert>
                )}

                {activeStep === 0 && (
                    <PlanDetailsStep
                        state={state}
                        errors={fieldErrors}
                        dispatch={dispatch}
                    />
                )}
                {activeStep === 1 && (
                    <DaysEditorStep
                        days={state.days}
                        dispatch={dispatch}
                        requestedDayId={requestedDayId}
                        onDaySelected={() => setRequestedDayId(null)}
                    />
                )}
                {activeStep === 2 && (
                    <RestrictionsReviewStep state={state} dispatch={dispatch} />
                )}

                <Divider sx={{ my: 3 }} />
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        disabled={submitting}
                        onClick={activeStep === 0 ? handleCancel : handleBack}
                    >
                        {activeStep === 0 ? t('nutrition.cancel') : t('nutrition.back')}
                    </Button>

                    {activeStep < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleNext}
                        >
                            {t('nutrition.next')}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={
                                submitting ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <CheckIcon />
                                )
                            }
                            disabled={submitting}
                            onClick={handleCreate}
                        >
                            {submitting
                                ? t('nutrition.editor.creating')
                                : t('nutrition.create')}
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

CreateNutritionPlan.propTypes = {
    initialTitle: PropTypes.string,
    initialDescription: PropTypes.string,
    initialData: PropTypes.object
};

export default CreateNutritionPlan;
